import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const services = [
  { name: "AI Development", href: "/services/ai-development" },
  { name: "AI Agents & Automation", href: "/services/ai-agents" },
  { name: "Custom Tools (Kill SaaS)", href: "/services/custom-tools" },
  { name: "Mobile App Development", href: "/services/mobile-development" },
  { name: "eCommerce Development", href: "/services/ecommerce-development" },
  { name: "Payment Gateways", href: "/services/payment-gateways" },
  { name: "Custom Development", href: "/services/custom-development" },
  { name: "WordPress & WooCommerce", href: "/services/wordpress-woocommerce" },
  { name: "Post-Launch Support", href: "/services/post-launch-support" },
  { name: "Laravel Development", href: "/services/laravel-development" },
  { name: "Shopify Development", href: "/services/shopify-development" },
];

const navLinks = [
  { name: "Case Studies", href: "/case-studies" },
  { name: "About", href: "/about" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-8">
        <Link to="/" className="flex flex-col">
          <span className="text-xl font-bold tracking-tight text-foreground">One<span className="text-primary">Pix</span></span>
          <span className="text-[10px] leading-tight text-muted-foreground hidden sm:block">AI & Custom Web Development</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
              Services <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full w-[480px] rounded-xl border border-border bg-card/95 p-3 shadow-2xl backdrop-blur-xl"
                >
                  <div className="grid grid-cols-2 gap-1">
                    {services.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        className={`block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted ${
                          location.pathname === s.href ? "text-primary" : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`rounded-lg px-4 py-2 text-sm transition-colors hover:text-foreground ${
                location.pathname === link.href ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {link.name}
            </Link>
          ))}

          <a
            href="https://wa.me/message/7LSTVCZDQZ27H1"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 inline-flex h-9 w-9 items-center justify-center rounded-lg text-[hsl(142,70%,45%)] transition-colors hover:bg-[hsl(142,70%,40%)]/10"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-4.5 w-4.5" />
          </a>

          <Link to="/contact">
            <Button variant="glow" size="sm" className="ml-2">
              Contact Us
            </Button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-foreground" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"}>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-border bg-background md:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              <p className="px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Services</p>
              {services.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  {s.name}
                </Link>
              ))}
              <div className="my-2 border-t border-border" />
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  {link.name}
                </Link>
              ))}
              <div className="my-2 border-t border-border" />
              <div className="flex gap-2 px-3">
                <a href="https://wa.me/message/7LSTVCZDQZ27H1" target="_blank" rel="noopener noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[hsl(142,70%,45%)] hover:bg-[hsl(142,70%,40%)]/10">
                  <MessageCircle className="h-5 w-5" />
                </a>
              </div>
              <Link to="/contact" onClick={() => setMobileOpen(false)}>
                <Button variant="glow" className="mt-3 w-full">Contact Us</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
